import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useToast,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Flex,
  useDisclosure,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import {
  DeliverableContainerReceiptActionsGQLType,
  ThreadGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { FilterButton, FilterType } from '../FilterButton';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { useCreateNewThreadWithCommentForDeliverableContainerMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/CreateNewThreadWithCommentForDeliverableContainer.gql.generated';
import { useNotifyNewCommentForDeliverableContainerMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/NotifyNewCommentForDeliverableContainer.gql.generated';
import {
  CommentEditorInput,
  CommentEditorCancelButton,
  CommentEditorCreateButton,
  CommentEditor,
} from '~/packages/common-frontend/components/Comments/CommentEditor';
import { CommentThread } from '~/packages/common-frontend/components/Comments/CommentThread';
import { ExternalOrgSelector } from '~/packages/common-frontend/components/Comments/ExternalOrgSelector';
import { Counter } from '~/packages/common-frontend/components/Counter';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';

const ContainerUpdatesDrawerBody = ({
  events,
  refetchThreads,
  receiptId,
  containerId,
  reportName,
  creditFacilityName,
}: {
  events: (
    | ThreadGQLType
    | (DeliverableContainerReceiptActionsGQLType & {
        version: number;
      })
  )[];
  refetchThreads: () => void;
  receiptId?: string;
  containerId: string;
  reportName: string;
  creditFacilityName: string;
}) => {
  const toast = useToast();

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => b.created_at - a.created_at),
    [events],
  );

  const eventsGroupedByMonth = _.groupBy(sortedEvents, (e) => {
    const fullDateStr = commonDate.epochToFullDateStr(e.created_at);
    const datetime = commonDate.fullDateStrToDateTime(fullDateStr);

    return `${datetime.monthLong} ${datetime.year}`;
  });

  const [
    notifyNewCommentForDeliverableContainerMutation,
  ] = useNotifyNewCommentForDeliverableContainerMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  return (
    <Flex direction="column" gap="1rem" alignItems="flex-start" width="full">
      {Object.entries(eventsGroupedByMonth).map(([key, values]) => (
        <Flex key={key} direction="column" width="full" gap="1rem">
          <Typography
            variant="detail"
            fontWeight="bold"
            TextProps={{ textAlign: 'center' }}
          >
            {key}
          </Typography>
          {values.map((val) => {
            if (val.__typename === 'DeliverableContainerReceiptActions') {
              return (
                <Flex key={val.id} direction="column">
                  <Typography variant="detail-muted">
                    {commonDate.epochToLocaleDateString(val.created_at)}
                  </Typography>
                  <Typography variant="detail">
                    {`${_.startCase(val.userFirstName)} ${_.startCase(
                      val.userLastName,
                    )}, ${_.startCase(val.organizationName)}, ${
                      val.action === 'requestRemediation'
                        ? 'requested remediation for'
                        : val.action.toLowerCase()
                    }`}{' '}
                    <Link
                      href={`/deliverables/${containerId}/final-output-report-versions/${val.receiptId}`}
                      fontWeight="bold"
                      color="blue.300"
                    >{`Version ${val.version} of ${reportName}`}</Link>{' '}
                    {`for ${creditFacilityName}.`}
                  </Typography>
                </Flex>
              );
            }
            if (val.__typename === 'Thread') {
              return (
                <Flex key={val.threadId} direction="column">
                  <CommentThread
                    thread={val}
                    refetchThread={refetchThreads}
                    onCreateNewComment={async (newCommentId) => {
                      await notifyNewCommentForDeliverableContainerMutation({
                        variables: {
                          containerId,
                          commentId: newCommentId,
                          receiptId,
                        },
                      });
                      refetchThreads();
                    }}
                  />
                </Flex>
              );
            }

            return null;
          })}
        </Flex>
      ))}
    </Flex>
  );
};

export const ContainerUpdatesDrawer = ({
  isOpen,
  onClose,
  deliverableContainerThreads,
  refetchThreads,
  receiptsActionLog,
  receiptId,
  containerId,
  externalOrgOptions,
  reportName,
  creditFacilityName,
}: {
  isOpen: boolean;
  onClose: () => void;
  deliverableContainerThreads: ThreadGQLType[];
  refetchThreads: () => void;
  receiptId?: string;
  receiptsActionLog: (DeliverableContainerReceiptActionsGQLType & {
    version: number;
  })[];
  containerId: string;
  externalOrgOptions: { value: string; label: string }[];
  reportName: string;
  creditFacilityName: string;
}) => {
  const { user } = useUserAuthOrThrow();
  const toast = useToast();

  const externalDisclosure = useDisclosure({ defaultIsOpen: false });
  const [selectedExternalPartyIds, setSelectedExternalPartyIds] = useState<
    string[]
  >([]);

  const [currentFilter, setCurrentFilter] = useState<FilterType>();

  const allEvents = useMemo(() => {
    if (currentFilter) {
      const {
        audienceFilter: audienceType,
        externalParticipantIds,
      } = currentFilter;
      const filteredThreads = deliverableContainerThreads.filter((t) => {
        const orgIdsForThread = t.threadPermissions.map(
          (tp) => tp.organizationId,
        );

        const threadIsInternal =
          orgIdsForThread.length === 1 &&
          orgIdsForThread[0] === user.organization.id;
        const threadIsExternal =
          _.difference(externalParticipantIds.sort(), orgIdsForThread.sort())
            .length === 0;

        switch (audienceType) {
          case 'internal':
            return threadIsInternal;
          case 'external':
            return threadIsExternal;
          case 'all':
            return threadIsInternal || threadIsExternal;
          default:
            return finleyAssertUnreachable(audienceType);
        }
      });

      return [...filteredThreads, ...receiptsActionLog];
    }
    return [...deliverableContainerThreads, ...receiptsActionLog];
  }, [
    deliverableContainerThreads,
    currentFilter,
    user.organization.id,
    receiptsActionLog,
  ]);

  const pinnedThreads = useMemo(
    () => deliverableContainerThreads.filter((thread) => thread.pinned),
    [deliverableContainerThreads],
  );

  const [
    createNewThreadWithCommentForDeliverableContainerMutation,
  ] = useCreateNewThreadWithCommentForDeliverableContainerMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const [
    notifyNewCommentForDeliverableContainerMutation,
  ] = useNotifyNewCommentForDeliverableContainerMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const [tabIndex, setTabIndex] = useState(0);

  const onCreateComment = async (comment: string) => {
    const organizationIds = [
      ...new Set([user.organization.id, ...selectedExternalPartyIds]),
    ];
    const newThreadResult = await createNewThreadWithCommentForDeliverableContainerMutation(
      {
        variables: {
          organizationIds,
          comment,
          authorUserId: user.id,
          deliverableContainerId: containerId,
        },
      },
    );
    const commentId = newThreadResult.data?.thread.comments[0].id;

    if (!commentId) {
      throw Error(
        `Failed to create comment. Missing new [commentId=${commentId}].`,
      );
    }

    await notifyNewCommentForDeliverableContainerMutation({
      variables: { containerId, commentId, receiptId },
    });

    refetchThreads();
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent pt={2}>
        <DrawerCloseButton />
        <DrawerHeader>Updates</DrawerHeader>
        <DrawerBody>
          <Flex direction="column" gap="1rem" alignItems="flex-start">
            <CommentEditor
              onCreate={onCreateComment}
              isDisabled={
                externalDisclosure.isOpen &&
                selectedExternalPartyIds.length === 0
              }
              left={
                <ExternalOrgSelector
                  disclosure={externalDisclosure}
                  externalOrgOptions={externalOrgOptions}
                  selectedIds={selectedExternalPartyIds}
                  onChange={setSelectedExternalPartyIds}
                />
              }
              right={
                <>
                  <CommentEditorCancelButton />
                  <CommentEditorCreateButton>
                    Send Comment
                  </CommentEditorCreateButton>
                </>
              }
            >
              <CommentEditorInput isEditable />
            </CommentEditor>
            <Tabs
              size="sm"
              index={pinnedThreads.length === 0 ? 0 : tabIndex}
              onChange={(index) => setTabIndex(index)}
              width="full"
            >
              <Flex gap="0.5rem" alignItems="center">
                <TabList flexGrow={1}>
                  <Tab marginTop={0}>All</Tab>
                  {pinnedThreads.length > 0 && (
                    <Tab marginTop={0} gap="0.5rem">
                      <span>Pinned</span>
                      <Counter count={pinnedThreads.length} />
                    </Tab>
                  )}
                </TabList>
                <FilterButton
                  externalOrgOptions={externalOrgOptions}
                  onApply={(newCurrentFilter) => {
                    setCurrentFilter(newCurrentFilter);
                  }}
                  currentFilter={currentFilter}
                />
              </Flex>

              <TabPanels py="1rem">
                <TabPanel padding="0">
                  <ContainerUpdatesDrawerBody
                    events={allEvents}
                    refetchThreads={refetchThreads}
                    receiptId={receiptId}
                    containerId={containerId}
                    reportName={reportName}
                    creditFacilityName={creditFacilityName}
                  />
                </TabPanel>
                {pinnedThreads.length > 0 && (
                  <TabPanel padding="0">
                    <ContainerUpdatesDrawerBody
                      events={pinnedThreads}
                      refetchThreads={refetchThreads}
                      receiptId={receiptId}
                      containerId={containerId}
                      reportName={reportName}
                      creditFacilityName={creditFacilityName}
                    />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
